import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, TextField, Typography } from "@mui/material";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import LoadingSpinner from "../../components/LoadingSpinner";
import BottomActionBar from "../../components/BottomActionBar";
import { INDIGO, SLATE } from "../../constants/Colors";
import { getStringParamsFromObject } from "../../utils";

const FIELD_LABEL_SX = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: SLATE[900],
  mb: "4px",
  display: "block",
};

const FIELD_DESC_SX = {
  fontSize: "0.8125rem",
  color: SLATE[500],
  lineHeight: 1.5,
  mb: 1,
};

const TEXTAREA_SX = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900], lineHeight: 1.6 },
};

const INITIAL_STATE = {
  topics: {
    internal_name: "topics",
    display_name: "Topics For Probing",
    prompt_input_raw: "",
    id: null,
    app_id: null,
  },
  topic_to_prob_mapping: {
    internal_name: "topic_to_prob_mapping",
    display_name: "Topics to Prob Mapping",
    prompt_input_raw: "",
    id: null,
    app_id: null,
  },
};

export default function SmartProbing() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const selectedAgentSiteCode = useSelector((state) => state?.apps?.selectedAgentSiteCode);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [state, setState] = useState(INITIAL_STATE);
  const [triggerPoint] = useState(selectedAgentSiteCode || "");
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const originalRef = useRef(null);

  useEffect(() => {
    setPageMeta({ description: "Configure topics and probing mappings for AI clarification." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    setLoading(true);
    const query = getStringParamsFromObject({ trigger_point: triggerPoint, types: "topics,topic_to_prob_mapping" });
    makeRequest(`/v1/apps/${currentApp.id}/ai_probings?${query}`, { method: "GET", authTokens: tokens })
      .then((probes) => {
        let next = INITIAL_STATE;
        if (Array.isArray(probes) && probes.length > 0) {
          const probings = {};
          probes.forEach((p) => { probings[p.internal_name] = p; });
          next = { ...INITIAL_STATE, ...probings };
        }
        originalRef.current = { topics: next.topics.prompt_input_raw, topic_to_prob_mapping: next.topic_to_prob_mapping.prompt_input_raw };
        setState(next);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentApp?.id, triggerPoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (type, value) => {
    setState((prev) => {
      const next = { ...prev, [type]: { ...prev[type], prompt_input_raw: value } };
      const orig = originalRef.current;
      setIsDirty(
        orig === null ||
        next.topics.prompt_input_raw !== orig.topics ||
        next.topic_to_prob_mapping.prompt_input_raw !== orig.topic_to_prob_mapping
      );
      return next;
    });
  };

  const handleSave = async () => {
    const updatedProbs = Object.keys(state).map((type) => ({
      ...state[type],
      trigger_point: triggerPoint,
      app_id: currentApp?.id,
      ...(state[type].id ? { id: state[type].id } : {}),
    }));

    if (!updatedProbs.length) return;

    setSaving(true);
    try {
      await makeRequest(`/v1/apps/${currentApp?.id}/ai_probings`, {
        method: "PUT",
        body: { ai_probings: updatedProbs, app_id: currentApp?.id },
        authTokens: tokens,
      });
      originalRef.current = { topics: state.topics.prompt_input_raw, topic_to_prob_mapping: state.topic_to_prob_mapping.prompt_input_raw };
      setIsDirty(false);
    } catch {
      // error toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading Smart Probing..." subMessage="Fetching probing configuration" />;

  return (
    <Box sx={{ width: "100%", pb: 10 }}>
      {/* Topics For Probing */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
            Topics For Probing
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>Topics</Typography>
          <Typography sx={FIELD_DESC_SX}>
            Specify the topics (e.g. Admissions, Tuition Fee, Programs) for which the AI should ask a clarifying question to provide a more detailed answer.
          </Typography>
          <TextField
            multiline
            minRows={10}
            maxRows={20}
            fullWidth
            value={state.topics.prompt_input_raw}
            onChange={(e) => handleChange("topics", e.target.value)}
            placeholder={"Financial Aid: Queries around scholarships, loans, and funding options.\nAdmissions: Queries around admission requirements and the application process.\nPrograms: Queries around degree programs offered across multiple fields."}
            sx={TEXTAREA_SX}
          />
        </Box>
      </Box>

      {/* Topics to Prob Mapping */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
            Topics to Prob Mapping
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>Mapping</Typography>
          <Typography sx={FIELD_DESC_SX}>
            For each topic, specify the dimensions that need to be clarified to give a more detailed answer.
          </Typography>
          <TextField
            multiline
            minRows={10}
            maxRows={20}
            fullWidth
            value={state.topic_to_prob_mapping.prompt_input_raw}
            onChange={(e) => handleChange("topic_to_prob_mapping", e.target.value)}
            placeholder={"'Topic': 'Financial Aid'\n'probing_query': 'Please specify your residency status and program type...'"}
            sx={TEXTAREA_SX}
          />
        </Box>
      </Box>

      <BottomActionBar
        disabledSave={!isDirty || saving}
        handleSave={handleSave}
        saving={saving}
      />
    </Box>
  );
}
